import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ChartData,
  DashboardError,
  DEFAULT_CHART_DATA_ITEM,
  MAX_CHART_DATA_ITEM,
  SeriesChartData,
  Utilization,
  WidgetUrlConfig
} from '@app/dashboard/model/widget-item';
import { catchError, map } from 'rxjs/operators';
import { WidgetResource } from '@app/shared/resources/widget.resource';
import {
  DataTypeNames,
  DateInterval,
  getDefaultInfluxQueryOutlineForBuilding,
  getDefaultMetricsInfluxQueryOutlineForBuilding,
  InfluxQueryOutline
} from '@app/dashboard/model/influx-data';
import { DashboardStateService } from '@app/dashboard/dashboard-filter/dashboard-state.service';
import { TimeUtils } from '@app/shared/utils/time.utils';
import { format } from 'date-fns';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class WidgetService {
  constructor(
    private readonly widgetResource: WidgetResource,
    private readonly dashboardStateService: DashboardStateService
  ) {}

  getAvgTagOccupancyPerPeriod(): Observable<SeriesChartData[] | DashboardError> {
    const building = this.dashboardStateService.currentBuilding;
    const state = this.dashboardStateService.currentFilterState;
    const url = WidgetUrlConfig.AVG_TAG_OCCUPANCY_PERIOD.url(building.id);
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(building, true);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      type: 'MOTION',
      start: this.getStartOfDateFromEpoch(state?.startDate, defaultPayload.zoneId) ?? defaultPayload.start,
      end: this.getEndOfDateFromEpoch(state?.endDate, defaultPayload.zoneId) ?? defaultPayload.end,
      floorIds: state?.floors?.map((floor) => floor.id) ?? defaultPayload.floorIds,
      tagIds: state?.tags?.map((tag) => tag.id) ?? defaultPayload.tagIds
    };

    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) => {
        const xAxisLabel = this.getLabelForInterval(res.dateInterval);
        return res.seriesChartData
          .filter((dataPoint) => this.dashboardStateService.allTags.some((tag) => tag.id === dataPoint.key))
          .map((dataPoint) => {
            const tag = this.dashboardStateService.tagIdToTagMap.get(dataPoint.key);
            const tagColor = tag?.color;
            return {
              name: tag?.name || dataPoint.key,
              series: dataPoint.series?.map((data) => ({
                name: data.name,
                value: Number(data.value.toFixed(5))
              })),
              meta: { color: tagColor || '000', xAxisLabel, yAxisLabel: 'Occupancy in %' }
            };
          });
      }),
      catchError(this.handleError)
    );
  }

  getEnergyConsumptionForBuilding(chartType: 'pie' | 'bar'): Observable<ChartData[] | DashboardError> {
    const url = (
      chartType === 'bar' ? WidgetUrlConfig.AVERAGE_ENERGY_CONSUMPTION : WidgetUrlConfig.ENERGY_CONSUMPTION_BY_TAGS
    ).url(this.dashboardStateService.currentBuilding.id);
    const state = this.dashboardStateService.currentFilterState;
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(this.dashboardStateService.currentBuilding, true);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      aggregationType: 'SUM',
      start: this.getStartOfDateFromEpoch(state?.startDate, defaultPayload.zoneId) ?? defaultPayload.start,
      end: this.getEndOfDateFromEpoch(state?.endDate, defaultPayload.zoneId) ?? defaultPayload.end,
      floorIds: state?.floors?.map((floor) => floor.id) ?? defaultPayload.floorIds,
      tagIds: state?.tags?.map((tag) => tag.id) ?? defaultPayload.tagIds
    };

    // TODO: the tag list should be updated by selectable tags when no tags are selected by the user
    // but there is an issue with the tenant tags which needs to be fixed

    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) =>
        (chartType === 'pie' ? res.chartData.slice(0, MAX_CHART_DATA_ITEM) : res.chartData)
          .filter((dataPoint) => this.dashboardStateService.allTags.some((tag) => tag.id === dataPoint.key))
          .map((dataPoint) => ({
            name: this.dashboardStateService.tagIdToTagMap.get(dataPoint.key)?.name || dataPoint.key,
            value: dataPoint.value,
            extra: { unit: res.unit.suffix }
          }))
      ),
      catchError(this.handleError)
    );
  }

  getTotalEnergyConsumption(): Observable<SeriesChartData[] | DashboardError> {
    const url = WidgetUrlConfig.TOTAL_ENERGY_CONSUMPTION.url(this.dashboardStateService.currentBuilding.id);
    const state = this.dashboardStateService.currentFilterState;
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(this.dashboardStateService.currentBuilding);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      aggregationType: 'SUM',
      start: state?.startDate
        ? TimeUtils.toBrowserTimeZone(state.startDate, defaultPayload.zoneId) * 1000
        : defaultPayload.start,
      end: state?.endDate
        ? TimeUtils.toBrowserTimeZone(state.endDate, defaultPayload.zoneId) * 1000
        : defaultPayload.end,
      floorIds: state?.floors?.map((floor) => floor.id) ?? defaultPayload.floorIds,
      tagIds: state?.tags?.map((tag) => tag.id) ?? defaultPayload.tagIds,
      groupBy: 'BUILDING'
    };
    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) => {
        const xAxisLabel = this.getLabelForInterval(res.dateInterval);
        return res.seriesChartData.map((dataPoint) => ({
          name: dataPoint.name,
          series: dataPoint.series?.map((data) => ({
            name: data.name,
            value: data.value,
            extra: { unit: res.unit.suffix }
          })),
          meta: { xAxisLabel, yAxisLabel: 'Energy consumption(kWh)' }
        }));
      }),
      catchError(this.handleError)
    );
  }

  getTagsOccupancy(utilization?: Utilization): Observable<ChartData[] | DashboardError> {
    const building = this.dashboardStateService.currentBuilding;
    const state = this.dashboardStateService.currentFilterState;
    const url = this.getOccupancyURL(utilization).url(building.id);
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(building, true);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      type: 'MOTION',
      start: this.getStartOfDateFromEpoch(state?.startDate, defaultPayload.zoneId) ?? defaultPayload.start,
      end: this.getEndOfDateFromEpoch(state?.endDate, defaultPayload.zoneId) ?? defaultPayload.end,
      floorIds: state?.floors?.map((floor) => floor.id) ?? defaultPayload.floorIds,
      tagIds: state?.tags?.map((tag) => tag.id) ?? defaultPayload.tagIds
    };

    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) =>
        this.getFilteredChartData(res.chartData, utilization, state?.tags?.length)
          .filter((dataPoint) => this.dashboardStateService.allTags.some((tag) => tag.id === dataPoint.key))
          .map((dataPoint) => ({
            name: this.dashboardStateService.tagIdToTagMap.get(dataPoint.key)?.name || dataPoint.key,
            value: dataPoint.value,
            extra: { unit: '%' }
          }))
      ),
      catchError(this.handleError)
    );
  }

  getCurrentEnergyConsumption(): Observable<string | DashboardError> {
    const url = WidgetUrlConfig.CURRENT_ENERGY_CONSUMPTION.url(this.dashboardStateService.currentBuilding.id);
    return this.widgetResource.getRequest(url).pipe(
      map((res) => `${res.singleValue} kWh`),
      catchError(this.handleError)
    );
  }

  getAvgTagOccupancyPerDay(): Observable<SeriesChartData[] | DashboardError> {
    const building = this.dashboardStateService.currentBuilding;
    const state = this.dashboardStateService.currentFilterState;
    const url = WidgetUrlConfig.AVG_TAG_OCCUPANCY_DAILY.url(building.id);
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(building, true);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      dateInterval: 'DAILY',
      type: 'MOTION',
      start: this.getStartOfDateFromEpoch(state?.startDate, defaultPayload.zoneId) ?? defaultPayload.start,
      end: this.getEndOfDateFromEpoch(state?.endDate, defaultPayload.zoneId) ?? defaultPayload.end,
      floorIds: state?.floors?.map((floor) => floor.id) ?? defaultPayload.floorIds,
      tagIds: state?.tags?.map((tag) => tag.id) ?? defaultPayload.tagIds
    };
    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) =>
        res.seriesChartData
          .filter((dataPoint) => this.dashboardStateService.allTags.some((tag) => tag.id === dataPoint.key))
          .map((dataPoint) => {
            const tag = this.dashboardStateService.tagIdToTagMap.get(dataPoint.key);
            const tagColor = tag?.color;
            return {
              name: tag?.name || dataPoint.key,
              series: dataPoint.series?.map((data) => ({
                name: data.name,
                value: Number(data.value.toFixed(5))
              })),
              meta: { color: tagColor || '000', yAxisLabel: 'Occupancy in %' }
            };
          })
      ),
      catchError(this.handleError)
    );
  }

  getCo2Produced(): Observable<string | DashboardError> {
    const url = WidgetUrlConfig.CO2_PRODUCED.url(this.dashboardStateService.currentBuilding.id);
    return this.widgetResource.getRequest(url).pipe(
      map((res) => `${res.singleValue} kg`),
      catchError(this.handleError)
    );
  }

  getEnergySaved(shouldApplyDate: boolean): Observable<string | DashboardError> {
    const building = this.dashboardStateService.currentBuilding;
    const state = this.dashboardStateService.currentFilterState;
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(building, shouldApplyDate);
    const startTime =
      shouldApplyDate && state?.startDate != null
        ? this.getStartOfDateFromEpoch(state.startDate, defaultPayload.zoneId)
        : defaultPayload.start;
    const endTime =
      shouldApplyDate && state?.endDate != null
        ? this.getEndOfDateFromEpoch(state.endDate, defaultPayload.zoneId)
        : defaultPayload.end;
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      type: 'LIGHT_OUTPUT_LEVEL',
      aggregationType: 'ENERGY_SAVING',
      start: startTime,
      end: endTime
    };

    const url = WidgetUrlConfig.ENERGY_SAVING.url(building.id);

    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) => `${res.singleValue / 1000} kWh`),
      catchError(this.handleError)
    );
  }

  getBuildingConsumptionComparison(): Observable<{ lastYear: string; currentYear: string } | DashboardError> {
    const building = this.dashboardStateService.currentBuilding;
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(building);
    const currentYear = new Date().getFullYear();
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      start: new Date(currentYear, 0, 1).getTime(),
      end: new Date(currentYear, 11, 31).getTime(),
      type: 'ENERGY_CONSUMPTION',
      aggregationType: 'SUM',
      dateInterval: 'YEARLY',
      groupBy: 'BUILDING'
    };

    const url = WidgetUrlConfig.TOTAL_ENERGY_CONSUMPTION.url(building.id);
    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) => {
        const seriesData = res.seriesChartData.flatMap((data) => data.series);
        const currentYear = new Date().getFullYear();
        const lastYear = currentYear - 1;
        const processedData = seriesData.reduce(
          (acc, data) => {
            if (data.name === currentYear.toString()) {
              acc.currentYear += data.value;
            } else if (data.name === lastYear.toString()) {
              acc.lastYear += data.value;
            }
            return acc;
          },
          { currentYear: 0, lastYear: 0 }
        );
        return {
          currentYear: processedData.currentYear + ' kWh',
          lastYear: processedData.lastYear + ' kWh'
        };
      }),
      catchError(this.handleError)
    );
  }

  getTotalNodes(): Observable<number | DashboardError> {
    const url = WidgetUrlConfig.TOTAL_NODES.url(this.dashboardStateService.currentBuilding.id);
    return this.widgetResource.getRequest(url).pipe(
      map((res) => res.singleValue),
      catchError(this.handleError)
    );
  }

  getUnresponsiveNodes(): Observable<number | DashboardError> {
    const url = WidgetUrlConfig.UNRESPONSIVE_NODES.url(this.dashboardStateService.currentBuilding.id);
    return this.widgetResource.getRequest(url).pipe(
      map((res) => res.singleValue),
      catchError(this.handleError)
    );
  }

  getOverallSystemHealth(): Observable<ChartData[] | DashboardError> {
    const url = WidgetUrlConfig.OVERALL_SYSTEM_HEALTH.url(this.dashboardStateService.currentBuilding.id);
    return this.widgetResource.getRequest(url).pipe(
      map((res) => [
        {
          name: '',
          value: res.singleValue
        }
      ]),
      catchError(this.handleError)
    );
  }

  getEnergyYearlyEstimation(): Observable<string | DashboardError> {
    const building = this.dashboardStateService.currentBuilding;
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(building);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      type: 'ENERGY_CONSUMPTION',
      aggregationType: 'SUM',
      dateInterval: 'YEARLY',
      groupBy: 'BUILDING'
    };

    const url = WidgetUrlConfig.ENERGY_ESTIMATION.url(building.id);
    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) => (res.singleValue === -1 ? 'N/A' : `${res.singleValue / 1000} kWh`)),
      catchError(this.handleError)
    );
  }

  getEnergyIntensity(): Observable<string | DashboardError> {
    const url = WidgetUrlConfig.ENERGY_INTENSITY.url(this.dashboardStateService.currentBuilding.id);
    const state = this.dashboardStateService.currentFilterState;
    const defaultPayload = getDefaultInfluxQueryOutlineForBuilding(this.dashboardStateService.currentBuilding, true);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      aggregationType: 'SUM',
      start: this.getStartOfDateFromEpoch(state?.startDate, defaultPayload.zoneId) ?? defaultPayload.start,
      end: this.getEndOfDateFromEpoch(state?.endDate, defaultPayload.zoneId) ?? defaultPayload.end,
      groupBy: 'BUILDING'
    };
    return (
      this.widgetResource
        .postRequest(url, payload)
        // Comparing with -1 as this value is returned when service have insufficient data for calculation
        .pipe(
          map((res) => (res.singleValue === -1 ? 'N/A' : `${res.singleValue} kWh/sqm`)),
          catchError(this.handleError)
        )
    );
  }

  getNodesRequiringMaintenance(): Observable<number | DashboardError> {
    const url = WidgetUrlConfig.NODES_WITH_FAULTS.url(this.dashboardStateService.currentBuilding.id);
    return this.widgetResource.getRequest(url).pipe(
      map((res) => res.singleValue),
      catchError(this.handleError)
    );
  }

  getAnalyticsData(type: DataTypeNames): Observable<ChartData[] | DashboardError> {
    const building = this.dashboardStateService.currentBuilding;
    const state = this.dashboardStateService.currentFilterState;
    const defaultPayload = getDefaultMetricsInfluxQueryOutlineForBuilding(building);
    const payload: InfluxQueryOutline = {
      ...defaultPayload,
      type,
      floorIds: state?.floors?.map((floor) => floor.id) ?? defaultPayload.floorIds,
      tagIds: state?.tags?.map((tag) => tag.id) ?? defaultPayload.tagIds
    };
    const url = this.getAnalyticsUrl(type).url(building.id);
    return this.widgetResource.postRequest(url, payload).pipe(
      map((res) =>
        res.chartData.map((chartData) => ({
          name: format(TimeUtils.adjustDateToTimezone(chartData.time, building.timeZone), 'h:mm a'),
          value: chartData.value,
          extra: { unit: res.unit.suffix, hideName: true }
        }))
      ),
      catchError(this.handleError)
    );
  }

  getFilteredChartData = (chartData: ChartData[], utilization?: string, size?: number) => {
    if (utilization === 'MOST') {
      return chartData.slice(0, MAX_CHART_DATA_ITEM);
    } else if (utilization === 'LEAST') {
      return chartData.slice(-MAX_CHART_DATA_ITEM);
    } else {
      return chartData.slice(0, size && size !== 0 ? size : DEFAULT_CHART_DATA_ITEM);
    }
  };

  getOccupancyURL(utilization?: string): { url: (buildingId: number) => string } {
    if (utilization === 'MOST') {
      return WidgetUrlConfig.MOST_OCCUPIED_TAGS;
    } else if (utilization === 'LEAST') {
      return WidgetUrlConfig.LEAST_OCCUPIED_TAGS;
    } else {
      return WidgetUrlConfig.TAG_OCCUPANCY;
    }
  }

  getAnalyticsUrl(dataType: DataTypeNames): { url: (buildingId: number) => string } {
    switch (dataType) {
      case 'ENERGY_CONSUMPTION':
        return WidgetUrlConfig.ANALYTICS_ENERGY_CONSUMPTION;
      case 'AMBIENT_LIGHT_LEVEL':
        return WidgetUrlConfig.AMBIENT_LIGHT;
      case 'LIGHT_OUTPUT_LEVEL':
        return WidgetUrlConfig.LIGHT_LEVEL;
      case 'CONNECTION_QUALITY':
        return WidgetUrlConfig.CONNECTION_QUALITY;
      default:
        return WidgetUrlConfig.LIGHT_LEVEL;
    }
  }

  private getLabelForInterval(interval: DateInterval): string {
    switch (interval) {
      case 'HOURLY':
        return 'Hourly';
      case 'DAILY':
        return 'Days';
      case 'WEEKLY':
        return 'Weeks of the year';
      case 'MONTHLY':
        return 'Months of the year';
      case 'YEARLY':
        return 'Yearly';
      default:
        return '';
    }
  }

  private getStartOfDateFromEpoch(date: Date, timeZone: string): number {
    if (date == null) {
      return null;
    }
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) + TimeUtils.getUTCOffset(timeZone);
  }

  private getEndOfDateFromEpoch(date: Date, timeZone: string): number {
    if (date == null) {
      return null;
    }

    const oneDayMinusOneSecondInMillisecond = 86400000 - 1000;

    return (
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) +
      oneDayMinusOneSecondInMillisecond +
      TimeUtils.getUTCOffset(timeZone)
    );
  }

  private handleError(err: HttpErrorResponse): Observable<DashboardError> {
    const errorMessage =
      'We encountered an issue while attempting to load the data for this widget. Please refresh the widget to try again.';
    return of(new DashboardError(errorMessage, err.message));
  }
}
